import * as SelectPrimitive from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import type { ReactNode } from 'react'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectProps {
  value: string
  onValueChange: (value: string) => void
  options: SelectOption[]
  /** Lets a <Field> label point at the trigger. */
  id?: string
}

export function Select({ value, onValueChange, options, id }: SelectProps): ReactNode {
  return (
    <SelectPrimitive.Root value={value} onValueChange={onValueChange}>
      <SelectPrimitive.Trigger
        id={id}
        className="inline-flex h-[26px] min-w-[104px] items-center justify-between gap-2 rounded-md border border-border bg-surface px-2 text-[12px] leading-none text-fg transition-colors hover:border-border-strong disabled:pointer-events-none disabled:opacity-45"
      >
        <SelectPrimitive.Value />
        <SelectPrimitive.Icon asChild>
          <ChevronDown size={14} strokeWidth={1.5} className="text-fg-muted" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        {/* `popper` rather than the default `item-aligned`: the menu must never
            overlap its own trigger inside a 480px-tall panel. */}
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className="z-50 overflow-hidden rounded-md border border-border bg-surface-raised"
        >
          <SelectPrimitive.Viewport className="max-h-[220px] scrollbar-thin p-1">
            {options.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                className="flex cursor-default items-center gap-2 rounded-sm py-1 pr-3 pl-1.5 text-[12px] leading-none text-fg outline-none select-none data-[highlighted]:bg-accent-soft data-[state=checked]:text-accent"
              >
                <span className="flex w-3.5 shrink-0 justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <Check size={14} strokeWidth={1.5} />
                  </SelectPrimitive.ItemIndicator>
                </span>
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}
